import type { FindFn } from "@shared/models/analytics/base"
import { createBlankEvent } from "@shared/models/analytics/base"
import type { CourseEditCalendar } from "@shared/models/analytics/course"
import { getPossessiveForPlanbook } from "@shared/models/analytics/planbook"
import type { Course, CourseCalendarDateCustom } from "@shared/models/course"
import Patch from "@shared/models/patch-updater"
import type { PreparedAction } from "@shared/actions/action-doc-builder"
import { findCourseDateCustom } from "cc-frontend/utils/filter-dates/course"
import { assign, partial } from "lodash-es"
import moment from "moment"
import type { Narrative } from "@shared/actions/create-narrative"
import defaultCourseDateActivity from "./default-course-date-activity"
import defaultCourseDateContext from "./default-course-date-context"
import defaultCourseDateModels from "./default-course-date-models"

interface Payload {
  dateString: string
  course: Course
  shouldAddCourseDate: boolean
  courseDateToAdd: CourseCalendarDateCustom
}

export default function courseDateForceOnAction(payload: Payload): PreparedAction {
  return {
    name: "COURSE_DATE_FORCE_ON",
    payload: payload,
    patches: patches(payload),
    undoPatches: undoPatches(payload),
    narrative: partial(narrative, payload),
  }
}

function patches(payload: Payload) {
  // Add courseDateToAdd if necessary
  let patch
  if (payload.shouldAddCourseDate) {
    patch = Patch.create("course", payload.course.id).push(
      "attributes.calendar.dates",
      payload.courseDateToAdd
    )
  } else {
    let courseDate = findCourseDateCustom(payload.course, payload.dateString)!
    patch = Patch.create("course", payload.course.id, "attributes.calendar.dates", {
      id: courseDate.id,
    }).set("embedded", "attributes.isForcedOn", true)
  }

  patch
    .push("attributes.calendar.datesForcedOn", payload.dateString)
    .inc("attributes.calendar.version", 1)

  return [patch.patch]
}

function undoPatches(payload: Payload) {
  let patch
  if (payload.shouldAddCourseDate) {
    patch = Patch.create("course", payload.course.id).pull("attributes.calendar.dates", {
      id: payload.courseDateToAdd.id,
    })
  } else {
    let courseDate = findCourseDateCustom(payload.course, payload.dateString)!
    patch = Patch.create("course", payload.course.id, "attributes.calendar.dates", {
      id: courseDate.id,
    }).set("embedded", "attributes.isForcedOn", false)
  }

  patch
    .pull("attributes.calendar.datesForcedOn", payload.dateString)
    .inc("attributes.calendar.version", 1)

  return [patch.patch]
}

function narrative(payload: Payload, findFn: FindFn, userId): Narrative {
  let models = defaultCourseDateModels(payload, findFn, userId)
  let formattedDateString = moment(payload.dateString).format("ddd, MMM. D, YYYY")
  let courseTitle = payload.course.attributes.title
  let context = defaultCourseDateContext(payload, models)
  let newContext = assign({}, context, { forcedOn: true })
  let newEvent: CourseEditCalendar = {
    ...createBlankEvent(),
    root_id: models.planbook.id,
    root_type: "planbook",
    date_plan_scheduled_for: payload.dateString,

    primary_verb: "edit",
    primary_possessive: getPossessiveForPlanbook(findFn, userId, models.planbook.id),

    primary_object_type: "course",
    primary_object_id: payload.course.id,
    primary_object_specifier: "calendar",
    primary_object_id_and_ancestor_ids: [payload.course.id, models.planbook.id],

    adverbial_verb: "toggling",

    adverbial_object_type: "lesson_slot",
    adverbial_object_id: payload.dateString,
    adverbial_object_specifier: "on",
    adverbial_object_id_and_ancestor_ids: [payload.course.id, models.planbook.id],
  }

  return {
    title: "Turned Class Back On",
    titleForAnalytics: "Lesson - Toggle On",
    description: `Turned your ${courseTitle} class back on for ${formattedDateString}`,
    context: newContext,
    activity: defaultCourseDateActivity(payload, models),
    newAnalyticsEvents: [newEvent],
  }
}
